import { useMemo } from 'react';
import { Image, type StyleProp, View, type ViewStyle } from 'react-native';

import type { MatchedCoach } from '@/modules/api';

import { useImageAspectRatio } from './hooks/useImageAspectRatio';
import { TopCoachBadge } from './TopCoachBadge';

// Fallback ratio (landscape) used until the photo's real dimensions load.
const FALLBACK_IMAGE_RATIO = 368 / 233;

type CoachImageProps = {
  coach: Pick<MatchedCoach, 'displayName' | 'image'>;
  /** Caller-controlled box: width, margins, borderRadius, minHeight / height, etc. */
  containerStyle?: StyleProp<ViewStyle>;
  /**
   * - `fit` (default): the container is sized from the photo's natural aspect
   *   ratio so the whole image shows without cropping. The caller sets the width
   *   (e.g. '100%', 320) via `containerStyle`, or caps the box with
   *   `maxWidth` / `maxHeight` (see below).
   * - `fill`: the caller's box (fixed height / stretch / minHeight) governs and
   *   the photo cover-crops to fill it — for the two-column card hero.
   */
  sizing?: 'fit' | 'fill';
  /**
   * `fit` only — cap the rendered box. The photo keeps its aspect ratio and is
   * scaled to fit within these bounds (whichever is hit first), so it never
   * exceeds the cap and is never cropped. Pass one or both. When both are set
   * the computed width/height replace the caller's `containerStyle` width.
   */
  maxWidth?: number;
  maxHeight?: number;
  showTopCoach?: boolean;
  badgePosition?: 'center' | 'right';
};

/**
 * Reusable coach photo. Regulates image dimensions in one place: it measures the
 * photo and, in `fit` mode, sizes its container to the natural aspect ratio so
 * coach photos of varying dimensions render fully (no cropping) — optionally
 * bounded by `maxWidth` / `maxHeight`. Optionally overlays the "Top coach" badge.
 */
export function CoachImage({
  coach,
  containerStyle,
  sizing = 'fit',
  maxWidth,
  maxHeight,
  showTopCoach = false,
  badgePosition = 'center',
}: CoachImageProps) {
  const ratio = useImageAspectRatio(coach.image, FALLBACK_IMAGE_RATIO);

  // In `fit` mode, derive the box from the natural ratio, bounded by whichever
  // of maxWidth / maxHeight is provided. With both, scale the ratio to fit
  // inside the cap (no crop); with one, the other follows from the ratio; with
  // neither, fall back to a bare aspectRatio so the caller's width drives it.
  const fitStyle = useMemo<ViewStyle | null>(() => {
    if (sizing !== 'fit') return null;
    if (maxWidth != null && maxHeight != null) {
      const width = Math.min(maxWidth, maxHeight * ratio);
      const height = width / ratio;
      return { width, height };
    }
    if (maxHeight != null) return { height: maxHeight, width: maxHeight * ratio };
    if (maxWidth != null) return { width: maxWidth, aspectRatio: ratio };
    return { aspectRatio: ratio };
  }, [sizing, ratio, maxWidth, maxHeight]);

  return (
    <View style={[{ position: 'relative', overflow: 'hidden' }, containerStyle, fitStyle]}>
      {coach.image ? (
        <Image
          source={{ uri: coach.image }}
          style={{ width: '100%', height: '100%' }}
          resizeMode='cover'
          accessibilityLabel={coach.displayName}
        />
      ) : null}
      {showTopCoach && badgePosition === 'center' ? (
        <View
          pointerEvents='none'
          style={{
            position: 'absolute',
            bottom: 16,
            left: 0,
            right: 0,
            alignItems: 'center',
          }}
        >
          <TopCoachBadge />
        </View>
      ) : null}
      {showTopCoach && badgePosition === 'right' ? (
        <TopCoachBadge style={{ position: 'absolute', bottom: 16, right: 16 }} />
      ) : null}
    </View>
  );
}
